import React, { useState } from "react";
import {
  Box,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Switch,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { editUserJobDefaults, resetUserJobDefault } from "../../../../api/apiCall";
import { useAuth } from "../../../../auth/auth";
import { toast } from "react-toastify";
import { extractTime, fieldsToCheck, parseTime, toTimeString } from "../../utils";

type FormValues = {
    onDuty: string;
    offDuty: string;
    commutingHours: string;
    commutingMinutes: string;
    workload: number;
    attention: number;
    breakFrequencyHours?: string;
    breakFrequencyMinutes?: string;
    averageBreakHours?: string;
    averageBreakMinutes?: string;
    longestWorkHours?: string;
    longestWorkMinutes?: string;
    longestBreakHours?: string;
    longestBreakMinutes?: string;
    commutingTimeLocked?: boolean;
}

const IndividualDefault = ({userJobId, individualDefault, defaults, handleClose}: any) => {
    const {state} = useAuth();
    const [loading, setLoading] = useState(false);
    const [buttonLoading, setButtonLoading] = useState(false);

    const hasDifference = fieldsToCheck.some(
      key => defaults[key] !== individualDefault[key]
    );

    const {register, handleSubmit, control} = useForm<FormValues>({
        defaultValues: {
            onDuty: extractTime(individualDefault.startDayTime),
            offDuty: extractTime(individualDefault.endDayTime),
            commutingHours: parseTime(individualDefault.commutingTime).hours,
            commutingMinutes: parseTime(individualDefault.commutingTime).minutes,
            workload: individualDefault.workload,
            attention: individualDefault.attention,
            breakFrequencyHours: parseTime(individualDefault.breakFrequency).hours,
            breakFrequencyMinutes: parseTime(individualDefault.breakFrequency).minutes,
            averageBreakHours: parseTime(individualDefault.averageLengthOfBreaks).hours,
            averageBreakMinutes: parseTime(individualDefault.averageLengthOfBreaks).minutes,
            longestWorkHours: parseTime(individualDefault.longestWorkPeriodOfContinuousWork).hours,
            longestWorkMinutes: parseTime(individualDefault.longestWorkPeriodOfContinuousWork).minutes,
            longestBreakHours: parseTime(individualDefault.breakLengthAfterLongestWorkPeriod).hours,
            longestBreakMinutes: parseTime(individualDefault.breakLengthAfterLongestWorkPeriod).minutes,
            commutingTimeLocked: individualDefault.commutingTimeLocked,
        }
    });

    const onSubmit = async (data: any) => {
        try {
            setLoading(true);
            const response = await editUserJobDefaults(state.user?.token, {
                userJobId: userJobId,
                commutingTime: toTimeString(data.commutingHours, data.commutingMinutes),
                onDuty: `${data.onDuty}:00`,
                offDuty: `${data.offDuty}:00`,
                workload: Number(data.workload),
                attention: Number(data.attention),
                breakFrequency: toTimeString(data.breakFrequencyHours, data.breakFrequencyMinutes),
                averageLengthOfBreaks: toTimeString(data.averageBreakHours, data.averageBreakMinutes),
                longestWorkPeriodOfContinuousWork: toTimeString(data.longestWorkHours, data.longestWorkMinutes),
                breakLengthAfterLongestWorkPeriod: toTimeString(data.longestBreakHours, data.longestBreakMinutes),
                commutingTimeLocked: data.commutingTimeLocked,
            });

            if(response) {
                handleClose();
                toast.success("Individual defaults updated successfully");
            }
        } catch (error) {
            toast.error("Failed to update worker defaults. Please try again.");
        }
    }

    const resetIndividualDefaults = async () => {
        try {
            setButtonLoading(true);
            const response: any = await resetUserJobDefault(state.user?.token, userJobId);
            if(response?.status === 200) {
                handleClose();
                toast.success("Defaults changed successfully!");
            }
        } catch (error) {
            toast.error("Failed to update change defaults. Please try again.");
        }
    }

    return (
        <>
            <DialogTitle
                sx={{
                  textAlign: "center",
                  fontWeight: 700,
                  fontSize: "1.6rem",
                  letterSpacing: 0.5,
                  color: "#1e293b",
                  pb: 2,
                }}
            >
                Fatigue / Risk Assessment
            </DialogTitle>
            <DialogContent dividers sx={{ px: 4, py: 3 }}>
                <Grid container spacing={4}>
                  {/* LEFT SIDE */}
                    <Grid item xs={12} md={6}>
                         <Section
                            title="On Duty - Off Duty"
                            description="Please specify the typical start and end time of employee shifts."
                        >
                            <Box display="flex" gap={2} alignItems="center">
                                <TextField
                                    label="Start Time"
                                    type="time"
                                    size="small"
                                    sx={{ width: 180 }}
                                    InputLabelProps={{ shrink: true }}
                                    {...register("onDuty")}
                                />
                                <TextField
                                    label="End Time"
                                    type="time"
                                    size="small"
                                    sx={{ width: 180 }}
                                    InputLabelProps={{ shrink: true }}
                                    {...register("offDuty")}
                                />
                            </Box>

                            <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                                (Please specify the typical working hours)
                            </Typography>
                        </Section>
                       

                        {/* TYPE OF JOB - WORKLOAD */}
                        <Section
                            title="Type of Job: Workload"
                            description="The workload and/or work pace of the job is typically:"
                        >
                            <Controller
                                name="workload"
                                control={control}
                                defaultValue={2}
                                render={({ field }) => (
                                    <RadioGroup {...field}>
                                        <FormControlLabel
                                            value={"3"}
                                            control={<Radio />}
                                            label="Extremely demanding, no spare capacity"
                                        />
                                        <FormControlLabel
                                            value={"2"}
                                            control={<Radio />}
                                            label="Moderately demanding, little spare capacity"
                                        />
                                        <FormControlLabel
                                            value={"1"}
                                            control={<Radio />}
                                            label="Moderately undemanding, some spare capacity"
                                        />
                                        <FormControlLabel
                                            value={"0"}
                                            control={<Radio />}
                                            label="Extremely undemanding, lots of spare capacity"
                                        />
                                    </RadioGroup>
                                )}
                            />
                        </Section>

                        <Section
                            title="Type of Job: Attention"
                            description="The job typically requires continuous attention:"
                        >
                            <Controller
                                name="attention"
                                control={control}
                                defaultValue={2}
                                render={({ field }) => (
                                    <RadioGroup {...field}>
                                        <FormControlLabel
                                            value={3}
                                            control={<Radio />}
                                            label="All or nearly all the time"
                                        />
                                        <FormControlLabel
                                            value={2}
                                            control={<Radio />}
                                            label="Most of the time"
                                        />
                                        <FormControlLabel
                                            value={1}
                                            control={<Radio />}
                                            label="Some of the time"
                                        />
                                        <FormControlLabel
                                            value={0}
                                            control={<Radio />}
                                            label="Rarely or nearly none of the time"
                                        />
                                    </RadioGroup>
                                )
                            }/>
                        </Section>
                    </Grid>

                  {/* RIGHT SIDE */}
                    <Grid item xs={12} md={6}>
                        <Section
                          title="Commuting Time"
                          description="What is the typical commuting time of employees to or from work?"
                        >
                            <Box display="flex" gap={2} alignItems="center" mt={1}>
                              <TextField
                                label="Hours"
                                type="number"
                                size="small"
                                // disabled={isLocked}
                                {...register("commutingHours")}
                              />
                              <TextField
                                label="Minutes"
                                type="number"
                                size="small"
                                // disabled={isLocked}
                                {...register("commutingMinutes")}
                              />
                            </Box>
                            <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                              (Please specify the typical commuting time)
                            </Typography>

                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" color="textSecondary">
                                </Typography>
                                <Controller
                                    name="commutingTimeLocked"
                                    control={control}
                                    render={({ field }) => (
                                        <Switch
                                            checked={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                        />
                                    )}
                                />
                            </Box>
                        </Section>
                        <Section
                            title="Breaks"
                        >
                            <Typography variant="body2" mt={4} mb={1}>
                                How frequently (to the nearaset 15 mins) are rest breaks typically provided OR taken?
                            </Typography>
                            <Box display="flex" gap={2} mb={4}>
                                <TextField
                                    label="Hours"
                                    type="number"
                                    size="small"
                                    {...register("breakFrequencyHours")}
                                />
                                <TextField
                                    label="Minutes"
                                    type="number"
                                    size="small"
                                    {...register("breakFrequencyMinutes")}
                                />
                            </Box>

                            <Typography variant="body2" mb={1}>
                                What is the typical average length of these breaks (to the nearest 5 minutes) that are provided or taken?
                            </Typography>
                            <Box display="flex" gap={2} mb={4}>
                                <TextField
                                    label="Hours"
                                    type="number"
                                    size="small"
                                    {...register("averageBreakHours")}
                                />
                                <TextField
                                    label="Minutes"
                                    type="number"
                                    size="small"
                                    {...register("averageBreakMinutes")}
                                />
                            </Box>

                            <Typography variant="body2" mb={1}>
                                What is typically the longest (to the nearest 15 mins) period of continuous work before
                                a break?
                            </Typography>
                            <Box display="flex" gap={2} mb={4}>
                                <TextField
                                    label="Hours"
                                    type="number"
                                    size="small"
                                    {...register("longestWorkHours")}
                                />
                                <TextField
                                    label="Minutes"
                                    type="number"
                                    size="small"
                                    {...register("longestWorkMinutes")}
                                />
                            </Box>

                            <Typography variant="body2" mb={1}>
                              What is typically the length of the break taken after this
                              longest period of continuous work (to the nearest 5 minutes)?
                            </Typography>
                            <Box display="flex" gap={2} mb={2}>
                                <TextField
                                  label="Hours"
                                  type="number"
                                  size="small"
                                  {...register("longestBreakHours")}
                                />
                                <TextField
                                  label="Minutes"
                                  type="number"
                                  size="small"
                                  {...register("longestBreakMinutes")}
                                />
                            </Box>
                        </Section>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ justifyContent: "center", py: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    onClick={handleSubmit(onSubmit)}
                    style={{
                      width: 180,
                      color: 'white',
                      boxShadow:'none',
                      textTransform: "none",
                      backgroundColor: "#156bc2",
                    }}
                >
                    {loading ? (
                        <> <CircularProgress size={25} style={{color: 'white'}} /></>
                    ) : "Save"}
                </Button>
                    <Button
                        variant="contained"
                        onClick={resetIndividualDefaults}
                        disabled={!hasDifference || buttonLoading}
                        style={{
                            boxShadow: 'none',
                            backgroundColor: hasDifference ? "#6c757d" : "#c4c4c4ff",
                            textTransform: "none",
                            width: 180,
                            color: 'white'
                        }}
                    >
                        {buttonLoading ? (
                            <> <CircularProgress size={25} style={{color: 'white'}} /></>
                        ) : "Change to Default"}
                    </Button>
            </DialogActions>
        </>
    );
};

export default IndividualDefault;

interface SectionProps {
  title: string;
  description?: string;
  children?: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({
  title,
  description,
  children,
}) => {
  return (
    <Box
      sx={{
        mb: 2,
        p: 2.5,
        borderRadius: 2,
        backgroundColor: "white",
        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.025)",
        transition: "all 0.3s",
        "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5} mb={1}>
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
      </Box>

      {description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, lineHeight: 1.6 }}
        >
          {description}
        </Typography>
      )}

      {children}
    </Box>
  );
};
